import { useState } from "react";

import { type AgentStatus } from "../lib/api";
import { t, tn, type Language } from "../lib/i18n";
import { useRefresh } from "../lib/refresh";
import { formatRelative } from "../lib/relativeTime";
import { useNow } from "../lib/useNow";
import { Icon } from "./icons";

/** The `/status` fetch lifecycle. Owned by `Dashboard` and shared with
 * both `StatusStrip` and `VersionDetail` so the tiny liveness read runs
 * once per refresh, not once per consumer. */
export type AgentStatusState =
  | { kind: "loading" }
  | { kind: "loaded"; status: AgentStatus }
  | { kind: "error" };

interface StatusStripProps {
  statusState: AgentStatusState;
  language: Language;
  /** Live Teams channel name for the scope line. Null hides the line
   * (personal/static tab with no channel context). */
  channelName?: string | null;
}

type Tone = "ok" | "warn" | "alert" | "neutral";

// Square presence badge fill per health tone (data-layer mark → square,
// never a circle — the two-layer radius rule). `ok` carries a soft pulse,
// collapsed by `prefers-reduced-motion` via the global rule in tailwind.css.
const BADGE_CLASS: Record<Tone, string> = {
  ok: "bg-kwanza-accent",
  warn: "bg-kwanza-warnFill",
  alert: "bg-kwanza-alert",
  neutral: "bg-kwanza-inkSoft",
};

// A RUNNING agent that hasn't done anything substantive in this many
// hours reads as "running but quiet" rather than "running — last active".
// Presentation-only split; the backend enum stays coarse.
const ACTIVE_WITHIN_HOURS = 24;

interface Phrase {
  text: string;
  tone: Tone;
}

/** Map an :class:`AgentStatus` payload onto a localized health phrase +
 * dot tone. The backend enum is coarse (running/idle/stale); this splits
 * RUNNING into active/quiet and STALE into down/no-data for the user. */
function healthPhrase(
  status: AgentStatus,
  nowMs: number,
  language: Language,
): Phrase {
  const agoFrom = (iso: string | null): string =>
    iso ? formatRelative(Date.parse(iso), nowMs, language) : "";

  // No heartbeat AND no activity ever → "no data yet", not "down".
  if (status.last_heartbeat_at === null && status.last_activity_at === null) {
    return { text: t("health.unknown", language), tone: "neutral" };
  }

  if (status.status === "stale") {
    const ago = agoFrom(status.last_heartbeat_at ?? status.last_activity_at);
    return { text: tn("health.stale", { ago }, language), tone: "alert" };
  }
  if (status.status === "idle") {
    const ago = agoFrom(status.last_heartbeat_at);
    return { text: tn("health.idle", { ago }, language), tone: "warn" };
  }
  // running — split active vs quiet on substantive-activity recency
  const hsa = status.hours_since_activity;
  const active =
    status.last_activity_at !== null && hsa !== null && hsa <= ACTIVE_WITHIN_HOURS;
  if (active) {
    const ago = agoFrom(status.last_activity_at);
    return { text: tn("health.running.active", { ago }, language), tone: "ok" };
  }
  return { text: t("health.running.quiet", language), tone: "ok" };
}

/** Pinned status strip — the "is Kwanza running?" answer + a refresh
 * control + an "Updated X ago" freshness marker. Presentational: takes
 * the shared `/status` state; reads the shared reload signal from
 * `useRefresh` for the ⟳ button + freshness marker. Lives once at the
 * Dashboard level so it persists across tab switches by construction. */
export function StatusStrip({
  statusState,
  language,
  channelName = null,
}: StatusStripProps): JSX.Element {
  const { lastRefreshedAt, triggerReload } = useRefresh();
  const now = useNow(30_000);
  const [supportOpen, setSupportOpen] = useState(false);

  // A strip-only fetch failure degrades to a neutral "no data" line — it
  // must NOT take over the page (that's the ErrorPanel's job for the
  // primary load). Loading shows a muted placeholder phrase.
  const phrase: Phrase =
    statusState.kind === "loaded"
      ? healthPhrase(statusState.status, now, language)
      : statusState.kind === "error"
        ? { text: t("health.unknown", language), tone: "neutral" }
        : { text: t("loading", language), tone: "neutral" };

  const productName = t("app.product_name", language);
  const version =
    statusState.kind === "loaded" ? statusState.status.version : null;
  const heartbeatAt =
    statusState.kind === "loaded" ? statusState.status.last_heartbeat_at : null;
  const heartbeatAgo = heartbeatAt
    ? formatRelative(Date.parse(heartbeatAt), now, language)
    : t("invoice.unset", language);

  const updatedLabel =
    lastRefreshedAt === null
      ? t("status.updated_never", language)
      : `${t("status.updated_prefix", language)} ${formatRelative(
          lastRefreshedAt,
          now,
          language,
        )}`;

  return (
    <section
      aria-label={t("status.reload", language)}
      className="flex flex-wrap items-center justify-between gap-3 rounded-[2px] border border-kwanza-divider bg-kwanza-card p-3 shadow-sm"
      data-testid="status-strip"
      data-health={
        statusState.kind === "loaded" ? statusState.status.status : statusState.kind
      }
    >
      <div className="flex items-center gap-3">
        {/* Agent identity: round avatar + a SQUARE presence badge (data
         * layer → square, never a circle) coloured by health. The badge
         * carries the `status-dot` test id (health-tone assertions). */}
        <span className="relative inline-block shrink-0">
          <img
            src="/agent-avatar.webp"
            alt=""
            aria-hidden="true"
            width={42}
            height={42}
            className="h-[42px] w-[42px] rounded-full bg-kwanza-surface object-cover"
          />
          <span
            className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-[2px] ring-2 ring-kwanza-card ${
              BADGE_CLASS[phrase.tone]
            } ${phrase.tone === "ok" ? "animate-pulse" : ""}`}
            data-testid="status-dot"
            aria-hidden="true"
          />
        </span>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-kwanza-ink">
            {phrase.text}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-kwanza-inkSoft">
            <span className="font-medium text-kwanza-ink">{productName}</span>
            {channelName ? (
              <span
                className="inline-flex items-center gap-1"
                data-testid="status-scope"
              >
                <Icon name="shield-check" size={12} className="text-kwanza-accentText" />
                {tn("status.scope_line", { channel: channelName }, language)}
              </span>
            ) : null}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-kwanza-inkSoft" data-testid="status-updated">
          {updatedLabel}
        </span>
        <button
          type="button"
          onClick={triggerReload}
          data-testid="status-reload"
          aria-label={t("status.reload", language)}
          className="inline-flex items-center gap-1 rounded-md border border-kwanza-divider bg-kwanza-surface px-3 py-1 text-sm font-medium text-kwanza-accentText hover:bg-kwanza-accentSoft"
        >
          <Icon name="refresh" size={14} />
          {t("status.reload", language)}
        </button>

        {/* Support-info popover (ⓘ): version + last heartbeat tucked out of
         * the chrome. Replaces the always-on version chip and the
         * Diagnostics tab's VersionDetail (Task 7). */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setSupportOpen((v) => !v)}
            aria-expanded={supportOpen}
            aria-label={t("status.support_info", language)}
            data-testid="status-support-toggle"
            className="flex h-7 w-7 items-center justify-center rounded-md border border-kwanza-divider bg-kwanza-card text-kwanza-inkSoft hover:bg-kwanza-surface"
          >
            <Icon name="info" size={15} />
          </button>
          {supportOpen ? (
            <div
              role="dialog"
              aria-label={t("status.support_info", language)}
              data-testid="status-support-popover"
              className="absolute right-0 top-full z-10 mt-1 w-60 rounded-md border border-kwanza-divider bg-kwanza-card p-3 text-xs shadow-lg"
            >
              <div className="flex items-center justify-between gap-3 py-1">
                <span className="text-kwanza-inkSoft">
                  {t("status.version_label", language)}
                </span>
                <span
                  className="font-mono text-kwanza-ink"
                  data-testid="status-version-chip"
                >
                  v{version ?? "—"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 py-1">
                <span className="text-kwanza-inkSoft">
                  {t("version.last_heartbeat", language)}
                </span>
                <span className="text-kwanza-ink">{heartbeatAgo}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
